"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";

import { LandingRequestDemoModal } from "@/components/landing/layouts/lumina/LandingRequestDemoModal";
import {
  openRequestDemo,
  RequestAccessLink,
} from "@/components/marketing/shared/MarketingCTA";
import { NoScriptReveal } from "@/components/marketing/shared/NoScriptReveal";
import { useMarketingNavScroll } from "@/components/marketing/shared/useMarketingNavScroll";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { meridianStyles } from "./meridianStyles";

const securityPillars = [
  {
    number: "01",
    label: "Compliance",
    title: "SOC 2 controls",
    body: "Independent audit coverage, logs, source trails, and advisor approval gates support books-and-records review.",
  },
  {
    number: "02",
    label: "Encryption",
    title: "Encrypted by default",
    body: "Data is encrypted at rest and in transit. Secrets are managed through AWS Secrets Manager and KMS.",
  },
  {
    number: "03",
    label: "Zero data retention",
    title: "No model training",
    body: "Client data sent to the model provider is processed under zero-data-retention terms and is not used to train third-party models.",
  },
] as const;

const faqs = [
  {
    question: "What is Zom?",
    answer:
      "Zom is an operating and business intelligence platform for independent RIAs. It brings client, portfolio, workflow, compliance, and firm information together so advisors and firm leaders can see what needs attention, understand it in context, and prepare work for review.",
  },
  {
    question: "What information can Zom work with?",
    answer:
      "Depending on your setup, Zom can work with information from your CRM, custodial and portfolio systems, email, calendar, meeting transcripts, and client or firm documents you provide. We confirm each supported connection and explain exactly what Zom can and cannot access before onboarding.",
  },
  {
    question: "Do I need to replace my existing technology?",
    answer:
      "No. Zom is designed to work alongside the systems your firm already uses. Support varies by system, so we review your stack and confirm the available connections before setup.",
  },
  {
    question: "How does Zom handle security and compliance?",
    answer:
      "Zom has completed its SOC 2 Type II audit. It also uses encrypted data handling, role-based access, audit logging, and Claude Zero Data Retention. Client data sent to the model provider is processed under zero-data-retention terms and is not used to train third-party models. AI-generated work remains subject to advisor review and approval.",
  },
  {
    question:
      "How is Zom different from meeting-note tools or generic AI copilots?",
    answer:
      "Meeting-note tools capture a conversation. Generic copilots answer a prompt or draft content. Zom gets your firm. It uses connected information from your clients, documents, conversations, workflows, portfolios, compliance activity, and business operations to prepare work in context instead of starting from a blank prompt. Where available, Zom shows supporting source information for review. The advisor stays in control.",
  },
  {
    question: "Can Zom work across multiple clients or the entire firm?",
    answer:
      "Yes, within the data and permissions available to your firm. Principals can review information across households, clients, and advisor books. What Zom can show depends on connected sources and each user's role.",
  },
  {
    question: "Does Zom act automatically?",
    answer:
      "Zom does not act independently. It surfaces information, prepares work, and suggests next steps for review. The advisor decides what gets sent, recommended, approved, or implemented. It does not send client communications, execute trades, move money, or make final recommendations.",
  },
  {
    question: "What does it take to get started?",
    answer:
      "Request access through the website. We review your firm's technology stack, confirm supported connections and permissions, and select the first workflows to set up.",
  },
] as const;

function MarketingNav() {
  const { scrolled, hidden, setHidden } = useMarketingNavScroll();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleRequest = () => {
    setMobileOpen(false);
    openRequestDemo("handoff_nav");
  };

  return (
    <>
      <header
        className={cn(
          "zmk-nav",
          scrolled && "zmk-nav--scrolled",
          hidden && !mobileOpen && "zmk-nav--hidden",
        )}
        data-screen-label="Nav"
        onFocus={() => setHidden(false)}
      >
        <nav className="zmk-nav__inner" aria-label="Main navigation">
          <a className="zmk-nav__brand" href="#top" aria-label="Zom home">
            <Image
              src="/images/zom-marketing/logo-white-pictorial.png"
              alt="Zom"
              width={64}
              height={64}
              priority
            />
          </a>
          <div className="zmk-nav__links" data-desknav>
            <a href="/about">About</a>
            <a href="/security">Security</a>
            <Button
              type="button"
              variant="ghost"
              className="zmk-nav__request"
              onClick={handleRequest}
            >
              Request access
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="zmk-nav__menu"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((current) => !current)}
          >
            {mobileOpen ? <X aria-hidden /> : <Menu aria-hidden />}
          </Button>
        </nav>
      </header>

      <div className={cn("zmk-mobile-menu", mobileOpen && "is-open")}>
        <a href="/about" onClick={() => setMobileOpen(false)}>
          About
        </a>
        <a href="/security" onClick={() => setMobileOpen(false)}>
          Security
        </a>
        <Button type="button" className="zmk-button" onClick={handleRequest}>
          Request access
        </Button>
      </div>
    </>
  );
}

function HeroSection() {
  return (
    <section id="top" className="zmk-hero" data-screen-label="Hero">
      <div className="zmk-bloom" aria-hidden />
      <div className="zmk-grain" aria-hidden />
      <div className="zmk-hero__inner">
        <h1 className="zmk-display zmk-hero__title" data-sreveal>
          The operating intelligence layer for independent RIAs.
        </h1>
        <div className="zmk-hero__cta" data-sreveal>
          <p>
            Ask Zom anything about a client, a portfolio, or the economics of
            your firm. It draws from your connected data to give a cited answer
            and prepare the analysis, report, or next step for your review.
          </p>
          <Button asChild className="zmk-button">
            <RequestAccessLink source="handoff_hero">
              Request access
            </RequestAccessLink>
          </Button>
        </div>
      </div>
    </section>
  );
}

function SecuritySection() {
  return (
    <section
      id="security"
      className="zmk-security"
      data-screen-label="Security"
    >
      <div className="zmk-section-shell">
        <p className="zmk-mono" data-sreveal>
          Security
        </p>
        <h2 className="zmk-display" data-sreveal>
          Enterprise-grade security and controls.
        </h2>
        <p className="zmk-security__intro" data-sreveal>
          Where available, Zom shows supporting source information for review.
          Client data sent to the model provider is processed under
          zero-data-retention terms and is not used to train third-party models.
        </p>
        {/* Dedicated 1px hairline — left-origin scaleX draw, matching the
            reference export (not a scaled grid shell). */}
        <div className="zmk-sline" data-sline data-delay="200" aria-hidden />
        <div className="zmk-security__grid">
          {securityPillars.map((pillar) => (
            <article key={pillar.number} data-sreveal>
              <span>
                {pillar.number} {pillar.label}
              </span>
              <h3>{pillar.title}</h3>
              <p>{pillar.body}</p>
            </article>
          ))}
        </div>
        <div className="zmk-security__cta">
          <div className="zmk-sline" data-sline aria-hidden />
          <div className="zmk-security__cta-row" data-sreveal>
            <a className="zmk-text-link" href="/security">
              View the full security page <ArrowRight aria-hidden />
            </a>
          </div>
          <div className="zmk-sline" data-sline data-delay="150" aria-hidden />
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="zmk-faq" data-screen-label="FAQ">
      <div className="zmk-faq__grid" data-faq-grid>
        <div className="zmk-faq__head" data-faq-head data-sreveal>
          <p className="zmk-mono">Questions</p>
          <h2 className="zmk-display">Common questions</h2>
          <p>What Zom does, and what it takes to run at your firm.</p>
        </div>
        <div className="zmk-faq__items">
          {faqs.map((faq, index) => {
            const open = openIndex === index;

            return (
              <div className="zmk-faq__item" key={faq.question} data-sreveal>
                <Button
                  type="button"
                  variant="ghost"
                  className="zmk-faq__toggle"
                  aria-expanded={open}
                  onClick={() => setOpenIndex(open ? null : index)}
                >
                  <span>{faq.question}</span>
                  <i aria-hidden />
                </Button>
                <div className={cn("zmk-faq__body", open && "is-open")}>
                  <div className="zmk-faq__body-inner">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="zmk-faq__bottom" data-sreveal>
            <p>Still have a question?</p>
            <Button
              type="button"
              variant="ghost"
              className="zmk-outline-button"
              onClick={() => openRequestDemo("handoff_faq")}
            >
              Talk to our team
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function RequestSection() {
  return (
    <section
      id="request"
      className="zmk-request"
      data-screen-label="Request access"
    >
      <div className="zmk-bloom zmk-bloom--request" aria-hidden />
      <div className="zmk-grain" aria-hidden />
      <div className="zmk-request__inner">
        <h2 className="zmk-display" data-sreveal>
          Imagine your firm at full capacity.
        </h2>
        <p data-sreveal>Request access and see it on your own book.</p>
        <Button asChild className="zmk-button" data-sreveal>
          <RequestAccessLink source="handoff_request">
            Request access
            <ArrowRight aria-hidden />
          </RequestAccessLink>
        </Button>
      </div>
    </section>
  );
}

function MarketingFooter() {
  return (
    <footer className="zmk-footer" data-screen-label="Footer">
      <div className="zmk-footer__inner">
        <div className="zmk-footer__grid">
          <div className="zmk-footer__brand">
            <Image
              src="/images/zom-marketing/logo-white-pictorial.png"
              alt="Zom"
              width={64}
              height={64}
            />
            <p>Operating intelligence for independent RIAs.</p>
          </div>
          <div className="zmk-footer__links">
            <div>
              <span>About</span>
              <a href="/security">Security</a>
              <a href="/about">About</a>
            </div>
            <div>
              <span>Legal</span>
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
              <Button
                type="button"
                variant="ghost"
                className="zmk-footer__button"
                onClick={() => openRequestDemo("handoff_footer")}
              >
                Request access
              </Button>
            </div>
          </div>
        </div>
        <div className="zmk-footer__base">
          <p className="zmk-footer__disclaimer">
            Zom AI is an AI-powered software platform offered by Zom
            Technologies, LLC for use by registered investment advisers to
            access information, analyze data, and support operational and
            advisory workflows. Zom Technologies is a financial technology
            company and is not registered as a broker-dealer or investment
            adviser; any investment-related analysis, recommendations, or other
            outputs produced through Zom AI are provided for the RIA’s review
            and do not replace the RIA’s professional judgment, supervision, or
            compliance responsibilities. AI-generated outputs may be incomplete,
            inaccurate, or outdated and should be reviewed and verified by the
            RIA before being relied upon or communicated to clients.
          </p>
          <span>© 2026 Zom. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}

export default function MeridianLanding() {
  useEffect(() => {
    document.documentElement.classList.add("landing-page-scroll-container");
    return () => {
      document.documentElement.classList.remove(
        "landing-page-scroll-container",
      );
    };
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const revealItems = Array.from(
      document.querySelectorAll<HTMLElement>(".zmk-root [data-sreveal]"),
    );
    const lineItems = Array.from(
      document.querySelectorAll<HTMLElement>(".zmk-root [data-sline]"),
    );

    if (reduce) {
      [...revealItems, ...lineItems].forEach((item) =>
        item.classList.add("is-visible"),
      );
      return;
    }

    // The reference staggers siblings by 0.04s to 0.28s rather than revealing a
    // group all at once. Delay is by position among revealing siblings, capped
    // so a long list never trails far behind the fold. Hairlines with an
    // explicit data-delay keep their authored timing (0.15s / 0.2s).
    [...revealItems, ...lineItems].forEach((item) => {
      if (item.hasAttribute("data-delay")) return;
      const siblings = Array.from(item.parentElement?.children ?? []).filter(
        (child) =>
          child.hasAttribute("data-sreveal") ||
          child.hasAttribute("data-sline"),
      );
      const index = siblings.indexOf(item);
      if (index > 0) {
        item.style.transitionDelay = `${Math.min(index * 0.07, 0.28)}s`;
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" },
    );

    [...revealItems, ...lineItems].forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="zmk-root">
      <style dangerouslySetInnerHTML={{ __html: meridianStyles }} />
      <NoScriptReveal />
      <LandingRequestDemoModal />
      <MarketingNav />
      <main>
        <HeroSection />
        <SecuritySection />
        <FaqSection />
        <RequestSection />
      </main>
      <MarketingFooter />
    </div>
  );
}
